import { Tooltip } from "flowbite-react";
import React from 'react';
import TruncatedText from "../../../../components/common/TruncatedText";
import { defaultUserNames } from "../../../../constants/defaultUsers";
import { useResourceInfo } from "../../../../hooks/useResourceInfo";
import { getRoadmapDetails } from "../../../../services/roadmap";
import { getEntityTypeDisplay } from "../../../../utils/entityDisplayUtils";
import WorkUnitMiniViewCard from '../../../WorkUnits/components/WorkUnitsViewCard';
import { WorkUnitsViewCardSkeleton } from '../../../WorkUnits/components/WorkUnitsViewCardSkeleton';

interface ViewTitleProps {
  name: string;
  entityType?: string;
  type?: string;
  className?: string; // For customized styling of the container or title
  createdAt?: string;
  createdBy?: string;
  id?: string;
  integration_data?: {
    integration_id: string;
    integration_type: string;
    integration_url: string;
    integration_item_type: string;
    integration_status: string;
    integration_tags?: any;
  };
  parent_info?: {
    id: string;
    type: string;
    entityType: string;
  };
 
}

export const ViewTitle: React.FC<ViewTitleProps> = ({
  name,
  entityType,
  type,
  className = "",
  integration_data,
  createdAt,
  createdBy,
  id,
  parent_info,
}) => {
  const entityInfo = getEntityTypeDisplay(entityType, type);
  const Icon = entityInfo.icon;
  const { getResourceName } = useResourceInfo();

  const [parentData, setParentData] = React.useState<any>(null);
  const [loadingParent, setLoadingParent] = React.useState(false);

  React.useEffect(() => {
    const fetchParentInfo = async () => {
      if (parent_info?.id) {
        setLoadingParent(true);
        try {
          const data = await getRoadmapDetails(parent_info.id);
          setParentData(data);
        } catch (error) {
          console.error("Failed to fetch parent info", error);
        } finally {
          setLoadingParent(false);
        }
      }
    };

    fetchParentInfo();
  }, [parent_info?.id]);

  const getDisplayCreatorName = (id: string | undefined) => {
    if (!id) return "Cadence Pro";
    if (parseInt(id) <= 0) {
      return defaultUserNames[id] || "System";
    }
    return getResourceName(id);
  };

  const formatTooltipText = (text: string) => {
    if (!text) return "";
    return text
      .split(/[\s_]+/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const [copiedKey, setCopiedKey] = React.useState<string | null>(null);

  const copyToClipboard = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      window.setTimeout(() => setCopiedKey(null), 1500);
    } catch (err) {
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        setCopiedKey(key);
        window.setTimeout(() => setCopiedKey(null), 1500);
      } catch (_) { }
    }
  };

  const CopyIcon = ({ isCopied = false }: { isCopied?: boolean }) => (
    isCopied ? (
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
    ) : (
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
      </svg>
    )
  );

  return (
    <>
      <h2 className="smv-sub-header-title ">
        <TruncatedText
          text={name}
          className="text-sm font-semibold text-gray-900 dark:text-white leading-tight truncate max-w-[300px]"
          as="h3"
        />
      </h2>
      {Icon && (
        <Tooltip
          content={
            <>
              <div className="px-3 py-2 -m-3 text-slate-700 dark:text-slate-200 flex flex-col items-start bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg w-[300px] gap-2">
                {/* 1. Entity Type with Name */}

                <div className="flex">
                  <label className="text-[10px] font-medium text-slate-700 dark:text-slate-500">
                    Current Entity Information
                  </label>
                </div>
                <div className="flex flex-col w-full">
                  <TruncatedText alwaysShowTooltip={true} text={name} />

                  <div
                    className={`flex items-center gap-2 text-[8px] w-full pb-2 border-b border-slate-200 dark:border-slate-700 `}
                  >
                    <Icon className={`w-2.5 h-2.5 ${entityInfo.iconClass}`} />
                    <span className=" uppercase">
                      {entityInfo.label}
                      {integration_data?.integration_type
                        ? ` (${integration_data.integration_type})`
                        : ""}
                    </span>
                  </div>
                </div>

                {/* 2. Created By & At */}
                <div className="flex flex-col gap-1 text-xs w-full">
                  <div className="flex items-center gap-1.5 text-slate-500 dark:text-slate-400">
                    <span className="font-medium text-slate-700 dark:text-slate-300">
                      Created By:
                    </span>
                    <span>{getDisplayCreatorName(createdBy)}</span>
                  </div>
                  {createdAt && (
                    <div className="flex items-center gap-1.5 text-slate-500 dark:text-slate-400">
                      <span className="font-medium text-slate-700 dark:text-slate-300">
                        Created On:
                      </span>
                      <span>
                        {createdAt
                          ? new Date(createdAt).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })
                          : "N/A"}
                      </span>
                    </div>
                  )}
                </div>

                {/* 3. Entity ID */}

                {/* Integration Data (Existing) */}
                {integration_data?.integration_id && (
                  <div className="w-full pt-2 mt-1 border-t border-slate-200 dark:border-slate-700 space-y-1">
                    <div className="text-xs">
                      <span className="font-bold">Source: </span>
                      {formatTooltipText(integration_data.integration_type)}
                    </div>
                    <div className="text-xs">
                      <span className="font-bold">Status: </span>
                      {formatTooltipText(integration_data.integration_status)}
                    </div>
                    <div className="text-xs">
                      <span className="font-bold">Type: </span>
                      {formatTooltipText(
                        integration_data.integration_item_type,
                      )}
                    </div>

                    {/* Open in Source Link */}
                  </div>
                )}

                {id && (
                  <div className="flex items-center justify-between w-full pt-2 border-t border-slate-200 dark:border-slate-700">
                    <div
                      className="flex items-center gap-1 text-[9px] text-slate-500 dark:text-slate-400 cursor-pointer hover:text-slate-700 dark:hover:text-slate-200"
                      onClick={(e) => {
                        e.stopPropagation();
                        copyToClipboard(id, "ID");
                      }}
                    >
                      <span className="font-medium text-slate-700 dark:text-slate-300">
                        ID:
                      </span>
                      <span className="font-mono">{id}</span>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        copyToClipboard(id, "ID");
                      }}
                      className={`p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded transition-colors ${copiedKey === "ID" ? "text-green-500" : "text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"}`}
                      title="Copy ID"
                    >
                      <CopyIcon isCopied={copiedKey === "ID"} />
                    </button>
                  </div>
                )}

                {integration_data?.integration_id &&
                  (() => {
                    const integrationUrl =
                      integration_data.integration_url ||
                      integration_data.integration_tags?.integration_url;
                    return (
                      integrationUrl && (
                        <div className="pt-1 mt-1  border-slate-200 dark:border-slate-700 w-full">
                          <a
                            href={integrationUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-xs text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 font-medium transition-colors"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <svg
                              className="w-3.5 h-3.5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                              />
                            </svg>
                            Open in{" "}
                            {integrationUrl.includes("azure") ||
                            integrationUrl.includes("visualstudio")
                              ? "Azure DevOps"
                              : "Jira"}
                          </a>
                        </div>
                      )
                    );
                  })()}

                {parent_info && (
                  <div className="w-full flex flex-col border-t border-slate-200 dark:border-slate-700 ">
                    <label className="text-[10px] font-medium text-slate-700 dark:text-slate-500 mb-2 mt-2">
                      Parent Entity Information
                    </label>
                    {loadingParent ? (
                      <WorkUnitsViewCardSkeleton />
                    ) : parentData ? (
                      <a href={`/roadmaps/${parentData.id}`} target='_blank'>

                      <WorkUnitMiniViewCard
                        data={parentData}
                        onEdit={() => {}}
                        />
                        </a>
                    ) : (
                      <div className="text-xs text-gray-500">
                        Parent information unavailable
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          }
        >
          <div
            className={`${entityInfo.chipClass} px-2 py-1 flex items-center justify-center bg-gray-200 rounded-full w-fit  gap-2`}
          >
            <Icon className={`w-3 h-3 ${entityInfo.iconClass}`} />
            <span className={`${entityInfo.labelClass} text-[10px]`}>
              {entityInfo.label}
              {integration_data?.integration_type ? (
                <span className="uppercase whitespace-nowrap">
                  {" "}
                  ({formatTooltipText(integration_data.integration_type)})
                </span>
              ) : (
                ""
              )}
            </span>
          </div>
        </Tooltip>
      )}
    </>
  );
};
